/**
 * The one resize grip, for both edges (#1069 Story 7.2, D3).
 *
 * Two grips in two files diverged in eleven days — one kept the WAI-ARIA
 * window-splitter contract and one lost it, one drew a 36x4 pill in a 24px
 * target and one drew a 2px full-height line in an 8px one (deferred-work
 * `:322-324`). This file makes "they cannot diverge" structural rather than a
 * promise: one hit area, one pill, one accessible-name-and-value shape, one set
 * of state colours, chosen by `direction`.
 *
 * ── What lives here, and what deliberately does not ──────────────────────────
 *
 * This component is PRESENTATIONAL plus the widget contract. It renders a hit
 * area, a pill, and the ARIA/RN accessibility props built from the value range
 * it is handed. It owns no clamp, no drag helper and no key handler:
 * `attachComposerResizeDrag`, `createComposerDragResponder` and
 * `attachDockResizeDrag` keep their signatures and stay with their hosts, which
 * are the only things that know what a pixel of travel means on their axis.
 *
 * ── The three appearances (Story 7.2 consult, 2026-09-18) ────────────────────
 *
 *   rest      `inputBorderNeutral` #dddddd   an affordance, not a state
 *   dragging  `textSecondary`      #666666   5.74:1 on #fff, 5.16:1 on #f1f3f5
 *   focused   `textPrimary`        #333333   9.30:1 against the resting pill
 *
 * ⚠️ `colors.primary` IS NOT AVAILABLE TO THIS COMPONENT AT REST OR LIT. It is
 * 2.84:1 on white — the contrast failure Story 7.1 spent a story removing from
 * the panel's tabs, and the dock grip's old `gripLit` colour.
 *
 * The resting pill is intentionally low-contrast and that is conforming: SC
 * 1.4.11 binds state indication and the boundary needed to OPERATE a control.
 * The resting pill indicates no state, and the boundary is the 24px hit area,
 * not the 4px pill. The lit states do indicate state and both clear 3:1.
 *
 * ── Focus is a colour step, not a ring ───────────────────────────────────────
 *
 * The composer's two text fields step their border colour on focus and draw no
 * ring; a ringed grip 12px above a colour-stepped field is two focus languages
 * in one band. SC 2.4.11 is met by the WHOLE PILL changing colour (144px² of
 * change at 9.30:1), which is not less indicator than a 2px perimeter around
 * the same pill would be.
 *
 * ── Native (Story 7.2, Task 5) ───────────────────────────────────────────────
 *
 * `CorrespondenceDock` is web-only, so only the composer ever mounts this on a
 * phone. On native the grip is an `adjustable` with `increment`/`decrement`
 * actions and a live `accessibilityValue`, so a VoiceOver or TalkBack user can
 * resize by swipe — the function was pointer-only on native until now
 * (deferred-work `:308-309`). That is NOT a substitute for the web
 * `role="separator"`, which the 2026-09-08 spec forbids and which this
 * component still emits on web; the two contracts are platform-exclusive
 * branches of the same value range.
 */
import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';

import type {
  GestureResponderHandlers,
  StyleProp,
  ViewStyle,
} from 'react-native';

/**
 * The pill — a short centred grabber, not a bar spanning the edge. A bar reads
 * as a DIVIDER; a pill reads as something to take hold of, which is the
 * distinction the product review made. 36x4 matches the platform vocabulary
 * users already know (the iOS sheet grabber is 36x5, the Material drag handle
 * 32x4), and like both of those it is FIXED: a grabber that grew with its edge
 * would be a divider again at the dock's 700px height.
 */
export const GRIP_PILL_LENGTH = spacing.xl + spacing.lg; // 36
export const GRIP_PILL_THICKNESS = spacing.xs; // 4

/**
 * The TOUCH TARGET, a different quantity from the pill and much larger. WCAG
 * 2.2 SC 2.5.8 asks for 24x24 CSS pixels; this codebase carried a logged
 * finding against the dock's old 8px handle for missing it, and Story 7.2 D5
 * closes it by giving both edges this one number.
 */
export const GRIP_TARGET = spacing.md * 2; // 24
/** Slop, taking touch to 32 without costing a pixel of layout. */
export const GRIP_TARGET_SLOP = spacing.sm; // 8

export type ResizeGripDirection = 'horizontal' | 'vertical';

/** The keyboard event shape both hosts already hand their key handler. */
export interface ResizeGripKeyEvent {
  key?: string;
  shiftKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  preventDefault?: () => void;
}

export interface ResizeGripProps {
  /**
   * `'horizontal'` is a grip on a horizontal edge that moves things vertically
   * (the composer's card top); `'vertical'` is the dock's leading edge. The
   * word names the EDGE, which is also what `aria-orientation` names, so the
   * two can never be set to disagree.
   */
  direction: ResizeGripDirection;
  /**
   * Composes `${testIDPrefix}-handle` and `${testIDPrefix}-grip`. The four
   * strings this produces are contract: `correspondence-composer-resize-*` and
   * `correspondence-dock-resize-*`, addressed by 40-plus assertions.
   */
  testIDPrefix: string;
  /** The accessible name. `Resize message box` / `Resize discussion panel`. */
  name: string;
  /** The live value range, in pixels. Built into ARIA and RN in one place. */
  value: number;
  min: number;
  max: number;
  /** Without this a screen reader announces a bare number. */
  valueText: string;
  /** A drag is in progress. */
  dragging?: boolean;
  /** Keyboard focus, as the host decides to define it. */
  focusVisible?: boolean;
  /**
   * The web widget contract, off while the host says the control is unusable —
   * the dock's `handleUsable` gate, which keeps a closing panel unfocusable.
   * Defaults to on.
   */
  usable?: boolean;
  /** Where the hit area sits. Absolute positioning belongs to the host. */
  style?: StyleProp<ViewStyle>;
  /** Which way the slop goes. The hosts' edges differ; see their comments. */
  hitSlop?: { top: number; bottom: number; left: number; right: number };
  /** The node the host's drag helper subscribes to. */
  handleRef?: React.Ref<View>;
  onKeyDown?: (event: ResizeGripKeyEvent) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /** `panHandlers` from the host's `PanResponder`. Native only. */
  panHandlers?: Partial<GestureResponderHandlers>;
  /**
   * One step of the native `adjustable` role, in the direction the name says.
   * Absent means the platform offers no assistive-tech resize — which is the
   * dock's case, since the dock never mounts on native at all.
   */
  onAccessibilityAdjust?: (action: 'increment' | 'decrement') => void;
}

export const ResizeGrip: React.FC<ResizeGripProps> = ({
  direction,
  testIDPrefix,
  name,
  value,
  min,
  max,
  valueText,
  dragging = false,
  focusVisible = false,
  usable = true,
  style,
  hitSlop,
  handleRef,
  onKeyDown,
  onFocus,
  onBlur,
  panHandlers,
  onAccessibilityAdjust,
}) => {
  /**
   * ⚠️ Read PER RENDER, not at module scope. Both hosts do the same
   * (`Composer.tsx`, `CorrespondenceDock.tsx`), and `ComposerResize.test.tsx`'s
   * header records why: the suites flip `Platform.OS` per test, and
   * `setupTests.ts` has already set it to `ios` by the time this module is
   * imported. A module-scope read freezes `isWeb` false and silently drops the
   * whole web widget contract -- no `role`, no `tabIndex`, no `aria-value*` --
   * on both edges. Behaviour-neutral in the app, where `Platform.OS` is
   * constant; decisive under test.
   */
  const isWeb = Platform.OS === 'web';
  const horizontal = direction === 'horizontal';

  /**
   * WAI-ARIA window-splitter: a focusable separator carrying its own value
   * range, so assistive tech announces a resize control rather than a
   * decorative divider. ⚠️ `separator` is STRUCTURAL until it is focusable —
   * `aria-value*` is not supported on the non-focusable form — so the tab stop
   * and the range go together or neither goes.
   */
  const webProps =
    isWeb && usable
      ? ({
          tabIndex: 0,
          role: 'separator',
          'aria-orientation': horizontal ? 'horizontal' : 'vertical',
          'aria-valuenow': value,
          'aria-valuemin': min,
          // Floored so the range can never invert and announce itself invalid.
          'aria-valuemax': Math.max(min, max),
          'aria-valuetext': valueText,
          onKeyDown,
          onFocus,
          onBlur,
        } as const)
      : ({} as const);

  /**
   * The native half of the same range. `adjustable` + two actions is RN's
   * equivalent of the arrow keys, and it is the only way a VoiceOver or
   * TalkBack user can reach a function that is otherwise a pan gesture.
   */
  const nativeProps =
    !isWeb && onAccessibilityAdjust
      ? ({
          accessibilityRole: 'adjustable' as const,
          accessibilityActions: [
            { name: 'increment' as const },
            { name: 'decrement' as const },
          ],
          accessibilityValue: { min, max, now: value, text: valueText },
          onAccessibilityAction: (event: {
            nativeEvent: { actionName: string };
          }) => {
            const action = event.nativeEvent.actionName;
            if (action === 'increment' || action === 'decrement') {
              onAccessibilityAdjust(action);
            }
          },
        } as const)
      : ({} as const);

  /**
   * ⚠️ `dragging` outranks `focusVisible`, and the order is load-bearing.
   * `attachDockResizeDrag` focuses the handle at drag start, so with focus
   * winning, the drag colour is unreachable on the dock: a state that can never
   * be seen is not a state, and AC 13 requires each of the two surviving flags
   * to drive a visible one. Drag is also the more specific condition -- it is
   * transient and always accompanied by focus, never the reverse.
   */
  const pillState = dragging
    ? styles.pillDragging
    : focusVisible
    ? styles.pillFocused
    : null;

  return (
    <View
      ref={handleRef}
      style={[
        horizontal ? styles.handleHorizontal : styles.handleVertical,
        style,
      ]}
      testID={`${testIDPrefix}-handle`}
      accessibilityLabel={name}
      hitSlop={hitSlop}
      {...(isWeb ? {} : panHandlers ?? {})}
      {...webProps}
      {...nativeProps}
    >
      <View
        style={[
          horizontal ? styles.pillHorizontal : styles.pillVertical,
          pillState,
        ]}
        testID={`${testIDPrefix}-grip`}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  /** 24 tall, stretched along its edge; the pill centres inside it. */
  handleHorizontal: {
    height: GRIP_TARGET,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /** 24 wide, the mirror of the above on the other axis. */
  handleVertical: {
    width: GRIP_TARGET,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pillHorizontal: {
    width: GRIP_PILL_LENGTH,
    height: GRIP_PILL_THICKNESS,
    // Fully rounded, derived rather than a literal so the pill stays a pill if
    // the thickness ever moves.
    borderRadius: GRIP_PILL_THICKNESS / 2,
    backgroundColor: colors.inputBorderNeutral,
  },
  pillVertical: {
    width: GRIP_PILL_THICKNESS,
    height: GRIP_PILL_LENGTH,
    borderRadius: GRIP_PILL_THICKNESS / 2,
    backgroundColor: colors.inputBorderNeutral,
  },
  /** A drag in progress. 5.74:1 on #fff, 5.16:1 on #f1f3f5. */
  pillDragging: {
    backgroundColor: colors.textSecondary,
  },
  /** Keyboard focus. Darker than the drag, so the two states are two. */
  pillFocused: {
    backgroundColor: colors.textPrimary,
  },
});

export default ResizeGrip;
